<script>
    import { DateTime } from 'luxon';
    import { onMount } from 'svelte';
    import FilePond from 'svelte-filepond';
    import { createForm } from 'svelte-forms-lib';
    import { getNotificationsContext } from 'svelte-notifications';
    import { date, string, object } from 'yup';

    import State from './State.svelte';

    import Button from 'src/fields/Button.svelte';
    import Date from 'src/fields/Date.svelte';
    import ReturnToServiceSelect from 'src/fields/ReturnToServiceSelect.svelte';
    import Text from 'src/fields/Text.svelte';
    import TextArea from 'src/fields/TextArea.svelte';
    import {
        createStateChangeHandler,
        updateFormStateFields,
        getInitialSchema as stateInitialsSchema,
        getValidationSchema as stateValidationSchema,
    } from 'src/fields/State/utils';

    import { nonRoutineApi } from 'src/api';
    import { bindClass } from 'src/utils/forms';
    import { notifyError } from 'src/utils/notification';
    import { getType } from 'src/core/state';

    // Props

    export let uid;

    // Data

    let url = `${uid}/`;
    let pending = true;
    let success = false;
    let pond;

    const DEFAULT_STATE_FIELDS = ['date', 'hours'];
    const AIRFRAME = 0;
    const ENGINE = 1;

    const NON_ROUTINE_CLOSED = 1;
    const NON_ROUTINE_DEFERRED = 2;
    const NON_ROUTINE_DISCARDED = 4;

    let nonRoutine = {};

    const { addNotification } = getNotificationsContext();

    // Form

    const {
        form,
        state,
        errors,
        handleSubmit,
        updateValidateField,
    } = createForm({
        initialValues: {
            ...stateInitialsSchema(),
        },

        validationSchema: object({
            corrective_notes: string().required(),

            ...stateValidationSchema('resolved_'),

            return_to_service_notes: string().required(),

            resolved_by: string().required(),
            resolution_date: date().required(),
            resolver_cert_number: string().required(),
        }),

        async onSubmit(values) {
            try {
                const { data } = await nonRoutineApi.patch(url, values);
                nonRoutine = data;
                success = true;
            } catch (error) {
                notifyError({
                    addNotification,
                    message: 'An error occurred while submitting the form. Please contact ENG team: eng@boutiqueair.com.',
                });
            }
        },
    });

    // Methods

    const fetchNonRoutine = async () => {
        try {
            pending = true;
            const { data } = await nonRoutineApi(url);
            nonRoutine = data;
            handleSubmit();
        } catch (error) {
            notifyError({
                addNotification,
                message: 'An error occurred while fetching Non-Routine. Please contact ENG team: eng@boutiqueair.com.'
            });
        } finally {
            pending = false;
        }
    };

    const handleChange = (name, val) => updateValidateField(name, val);
    const createHandleChange = name => ({ detail }) => handleChange(name, detail);
    const stateChangeHandler = createStateChangeHandler(handleChange);

    // Events

    onMount(fetchNonRoutine);

    // Reactive

    $: showFinalMessage = success || nonRoutine && nonRoutine.resolution_date;

    $: equipment = nonRoutine?.tracked_by_info;
    $: eqType = getType(equipment?.type);

    $: dateInitial = DateTime.local().toISODate();
    $: hoursInitial = equipment?.hours;
    $: landingsInitial = equipment?.landings;
    $: cyclesInitial = equipment?.cycles;

    $: { updateFormStateFields($form, eqType) }

    $: fileServerConfig = nonRoutine.id && {
        url: `/api/nonroutine-attachment/`,
        process: {
            ondata(formData) {
                formData.append("non_routine", nonRoutine.id);
                return formData;
            },
            onerror(response) {
                notifyError({
                    addNotification,
                    message: 'An error has occurred while uploading the document. Please contact ENG team: eng@boutiqueair.com.',
                })
                console.error(response);
            },
        },
    };
</script>

<svelte:head>
    {#if nonRoutine.id}
        <title>Non routine #{nonRoutine.control_number}</title>
    {:else}
        <title>Resolve Non-Routine</title>
    {/if}
</svelte:head>

<main class="nr-resolve">
    <div class="nr-resolve__header">
        <h1 class="nr-resolve__title">Resolve Non-Routine</h1>
        <span class="nr-resolve__control-number">#{nonRoutine.control_number}</span>
    </div>

    {#if showFinalMessage}
        <section class="nr-resolve__main-message">
            <h1>Thank you!</h1>
            <h3>Correction has been successfully recorded.</h3>
        </section>
    {:else if nonRoutine.id}
        <section class="nr-resolve__about">
            <h3>About</h3>

            <strong>You're about to resolve {nonRoutine.tracked_by_info.name}.</strong>
            <div style="margin-top: 10px;">{nonRoutine.description}</div>
        </section>

        <section use:bindClass={{ state, name: 'corrective_notes' }}>
            <h3>Corrective information</h3>

            <label class="label required" for="corrective-notes">
                Notes
            </label>
            <TextArea
                id="corrective-notes"
                bind:value="{$form.corrective_notes}"
                on:change={createHandleChange('corrective_notes')}
            />
        </section>

        <section>
            <h3>Equipment state</h3>

            <State
                equipmentType={eqType}
                form={$form}
                {state}

                {dateInitial}
                {hoursInitial}
                {landingsInitial}
                {cyclesInitial}

                on:change={stateChangeHandler}
            />
        </section>

        <section>
            <h3>Return to service</h3>
            <div class="field" use:bindClass={{ state, name: 'return_to_service_notes' }}>
                <ReturnToServiceSelect
                    bind:value="{$form.return_to_service_notes}"
                    on:change={createHandleChange('return_to_service_notes')}
                />
            </div>
        </section>

        <section>
            <h3>Attachments</h3>
            <FilePond bind:this={pond} server="{fileServerConfig}" allowMultiple={true} name="file" />
        </section>

        <section class="nr-resolve__corrected-by">
            <h3>Corrected by</h3>

            <div class="nr-resolve__corrected-by__content">
                <div class="field" use:bindClass={{ state, name: 'resolved_by' }}>
                    <label class="label required" for="corrected-full-name">Full name</label>
                    <Text
                        id="corrected-full-name"
                        placeholder="Natalie Portman"
                        bind:value="{$form.resolved_by}"
                        on:change={createHandleChange('resolved_by')}
                    />
                </div>

                <div class="field" use:bindClass={{ state, name: 'resolution_date' }}>
                    <label class="label required" for="corrected-date">Date</label>
                    <Date
                        id="corrected-date"
                        bind:value="{$form.resolution_date}"
                        on:change={createHandleChange('resolution_date')}
                    />
                </div>

                <div class="field" use:bindClass={{ state, name: 'resolver_cert_number' }}>
                    <label class="label required" for="corrected-cert-number">Cert number</label>
                    <Text
                        id="corrected-cert-number"
                        placeholder="XXX 1234XXXXXX"
                        bind:value="{$form.resolver_cert_number}"
                        on:change={createHandleChange('resolver_cert_number')}
                    />
                </div>
            </div>
        </section>

        <section class="nr-resolve__button">
            <Button on:click={handleSubmit} disabled="{$state.isSubmitting}">
                Confirm the correction
            </Button>
        </section>
    {:else}
        <section class="nr-resolve__main-message">
            {#if pending}
                <p>Loading, please wait...</p>
            {:else}
                <h1>Not Found</h1>
                <p>Non-Routine had been corrected or does not exist.</p>
            {/if}
        </section>
    {/if}

    <section class="end-page-spacer"></section>
</main>

<style>
    .nr-resolve__header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
    }

    .nr-resolve__corrected-by__content {
        display: flex;
        column-gap: 20px;
    }

    .nr-resolve__button {
        margin-top: 20px;
    }

    .nr-resolve__button :global(button) {
        width: 100%;
    }
</style>
